/**
 * Google Maps 2D shape rendering.
 * Manages Polygon, Circle, and Polyline instances for TAK shapes.
 */

import type { TakShape, TakCircle, GeoPoint } from '@vtak/shared';
import { argbIntToHex, argbAlpha } from '@vtak/shared';
import type { ShapeDiff } from './shape-state';

/** Active 2D polygons keyed by shape UID */
const polygonMap2d = new Map<string, google.maps.Polygon>();

/** Active 2D circles keyed by shape UID */
const circleMap2d = new Map<string, google.maps.Circle>();

/** Active 2D polylines keyed by shape UID */
const polylineMap2d = new Map<string, google.maps.Polyline>();

/** Initialize the 2D shape renderer */
export function initShapes2D(): void {
  // No-op — maps are lazy-initialized on first diff
}

/** Apply a shape diff to the 2D map */
export function applyShapeDiff2D(map: google.maps.Map, diff: ShapeDiff): void {
  for (const uid of diff.removed) {
    removeShape2D(uid);
  }

  for (const shape of diff.updated) {
    removeShape2D(shape.uid);
    addShape2D(map, shape);
  }

  for (const shape of diff.added) {
    addShape2D(map, shape);
  }
}

/** Destroy all 2D shapes */
export function destroyShapes2D(): void {
  for (const p of polygonMap2d.values()) p.setMap(null);
  for (const c of circleMap2d.values()) c.setMap(null);
  for (const l of polylineMap2d.values()) l.setMap(null);
  polygonMap2d.clear();
  circleMap2d.clear();
  polylineMap2d.clear();
}

/** Add a shape to the 2D map */
function addShape2D(map: google.maps.Map, shape: TakShape): void {
  const strokeColor = argbIntToHex(shape.strokeColor);
  const fillColor = argbIntToHex(shape.fillColor);
  const fillOpacity = argbAlpha(shape.fillColor);

  switch (shape.shapeType) {
    case 'circle':
    case 'geofence':
      addCircle2D(map, shape, strokeColor, fillColor, fillOpacity);
      break;
    case 'route':
    case 'freeform':
    case 'measurement':
      addPolyline2D(map, shape, strokeColor);
      break;
    case 'polygon':
    case 'rectangle':
      addPolygon2D(map, shape, strokeColor, fillColor, fillOpacity);
      break;
  }
}

/** Add a circle to the 2D map */
function addCircle2D(
  map: google.maps.Map,
  shape: TakCircle,
  strokeColor: string,
  fillColor: string,
  fillOpacity: number,
): void {
  const circle = new google.maps.Circle({
    map,
    center: toLatLng(shape.center),
    radius: shape.radiusMeters,
    strokeColor,
    strokeWeight: shape.strokeWeight,
    fillColor,
    fillOpacity,
    clickable: false,
  });
  circleMap2d.set(shape.uid, circle);
}

/** Add a polygon to the 2D map */
function addPolygon2D(
  map: google.maps.Map,
  shape: TakShape & { vertices: GeoPoint[] },
  strokeColor: string,
  fillColor: string,
  fillOpacity: number,
): void {
  const polygon = new google.maps.Polygon({
    map,
    paths: shape.vertices.map(toLatLng),
    strokeColor,
    strokeWeight: shape.strokeWeight,
    fillColor,
    fillOpacity,
    clickable: false,
  });
  polygonMap2d.set(shape.uid, polygon);
}

/** Add a polyline to the 2D map */
function addPolyline2D(
  map: google.maps.Map,
  shape: TakShape,
  strokeColor: string,
): void {
  const path = extractPolylinePath(shape);
  const polyline = new google.maps.Polyline({
    map,
    path: path.map(toLatLng),
    strokeColor,
    strokeWeight: shape.strokeWeight,
    clickable: false,
  });
  polylineMap2d.set(shape.uid, polyline);
}

/** Remove a shape from the 2D map by UID */
function removeShape2D(uid: string): void {
  const polygon = polygonMap2d.get(uid);
  if (polygon) { polygon.setMap(null); polygonMap2d.delete(uid); }

  const circle = circleMap2d.get(uid);
  if (circle) { circle.setMap(null); circleMap2d.delete(uid); }

  const polyline = polylineMap2d.get(uid);
  if (polyline) { polyline.setMap(null); polylineMap2d.delete(uid); }
}

/** Extract polyline path from a shape with vertices or waypoints */
function extractPolylinePath(shape: TakShape): GeoPoint[] {
  if ('vertices' in shape) return shape.vertices;
  if ('waypoints' in shape) return shape.waypoints.map((wp) => wp.position);
  if ('origin' in shape) return [shape.origin];
  return [];
}

/** Convert GeoPoint to Google Maps LatLngLiteral */
function toLatLng(p: GeoPoint): google.maps.LatLngLiteral {
  return { lat: p.lat, lng: p.lon };
}
